import { ref, type Ref } from 'vue';
import type { FeatureGateCatalogItem } from './provider';
import { getFeatureFlagProvider, onFeatureFlagProviderChange } from './registry';

/**
 * Module-level reactive cache for the feature-gate catalog. Shared across every caller
 * (the DevTools panel and {@link useFeatureGate}'s warning path) so all consumers see one
 * catalog and a single fetch populates everyone. @abax/platform is not a Module Federation
 * singleton, so this cache is per-copy of the module — same scope as the override store.
 */
const catalog: Ref<FeatureGateCatalogItem[]> = ref([]);
const isLoading = ref(false);

// Tracks whether a fetch has ever completed, so a second caller doesn't refetch on mount.
let hasFetched = false;
// The in-flight fetch promise. Concurrent callers (and a refresh during a first fetch)
// all share whatever is current here — never more than one provider call at a time.
let inflight: Promise<void> | undefined;
// Monotonic fetch generation. A forced refresh bumps it and starts a newer fetch; only
// the latest generation is allowed to write the cache, so an orphaned earlier fetch that
// resolves late can never clobber the result of the fetch that superseded it.
let generation = 0;

/**
 * Start a catalog fetch tagged with the current generation. `force` (a refresh) bumps the
 * generation first, superseding any in-flight fetch: the older fetch still runs to
 * completion but its result is discarded, and `inflight` is repointed at the newer fetch so
 * concurrent callers await the freshest data.
 */
function fetchCatalog(force = false): Promise<void> {
  if (force) {
    generation += 1;
  } else if (inflight) {
    // Idle de-dupe: a non-forced call joins the fetch already in flight.
    return inflight;
  } else {
    generation += 1;
  }

  const token = generation;

  const provider = getFeatureFlagProvider();
  const list = provider?.listCatalog;

  const settle = (items: FeatureGateCatalogItem[]): void => {
    // Only the latest generation writes the cache and clears the shared handle.
    if (token !== generation) return;
    catalog.value = items;
    hasFetched = true;
    isLoading.value = false;
    inflight = undefined;
  };

  // No provider or no catalog support — settle synchronously to an empty catalog.
  if (!provider || !list) {
    const promise = Promise.resolve().then(() => settle([]));
    inflight = promise;
    return promise;
  }

  isLoading.value = true;
  const promise = (async (): Promise<void> => {
    // Wait for provider readiness first — the shell's provider gates this on auth, so a
    // catalog fetch that fired pre-auth would otherwise settle empty. Providers without
    // the hook resolve immediately.
    if (provider.waitForInitialization) {
      try {
        await provider.waitForInitialization();
      } catch {
        // Readiness failure shouldn't abort the listing attempt.
      }
    }
    // A forced refresh may have superseded this fetch while it awaited readiness.
    if (token !== generation) return;
    try {
      const items = await list.call(provider);
      settle(Array.isArray(items) ? items : []);
    } catch {
      settle([]);
    }
  })();

  inflight = promise;
  return promise;
}

// Subscribe (once) to provider (re-)registration so a panel that read an absent or
// earlier provider re-fetches when the host wires its real one. Covers the shell wiring
// its provider after the panel mounts and dev-shell's async catalog producer registering
// a `listCatalog` provider mid-session. Lazily armed by the first catalog consumer so
// importing this module stays side-effect free.
let providerSubscribed = false;
function ensureProviderSubscription(): void {
  if (providerSubscribed) return;
  providerSubscribed = true;
  onFeatureFlagProviderChange(() => {
    void fetchCatalog(true);
  });
}

export interface UseFeatureFlagCatalogResult {
  /** Reactive catalog. Empty until the first fetch resolves (or when unsupported). */
  catalog: Ref<FeatureGateCatalogItem[]>;
  /** True while a catalog fetch is in flight. */
  isLoading: Ref<boolean>;
  /** Force a re-fetch from the current provider. */
  refresh: () => Promise<void>;
}

/**
 * Read the feature-gate catalog reactively. On first use it fetches from the registered
 * provider's `listCatalog` and caches the result at module scope; later callers reuse the
 * cache without refetching. Gracefully yields an empty catalog and never throws when no
 * provider is registered or the provider has no `listCatalog`.
 */
export function useFeatureFlagCatalog(): UseFeatureFlagCatalogResult {
  ensureProviderSubscription();
  if (!hasFetched && !inflight) {
    void fetchCatalog();
  }
  return {
    catalog,
    isLoading,
    // A refresh always re-hits the provider; forcing bumps the generation so it
    // supersedes (rather than orphans) any first fetch still in flight.
    refresh: () => fetchCatalog(true),
  };
}

/**
 * Synchronous read of the cached catalog. Never triggers a fetch — used by
 * {@link useFeatureGate}'s dev-mode typo warning, which must stay side-effect free.
 */
export function getCachedCatalog(): FeatureGateCatalogItem[] {
  return catalog.value;
}

/** @internal Test/HMR helper — clears the cached catalog and fetch state. */
export function __resetFeatureFlagCatalog(): void {
  catalog.value = [];
  isLoading.value = false;
  hasFetched = false;
  inflight = undefined;
  providerSubscribed = false;
  // Bump so any fetch still in flight from before the reset can never write back.
  generation += 1;
}
